import React from 'react';
import { Badge, Button } from 'react-bootstrap';
import FormattedDate from '../../../../../../components/date/FormattedDate';
import { formatTime24h } from '../../../../../../utils/dateUtils';
import { useNamespacedTranslation } from '../../../../../../hooks/useNamespacedTranslation';
import FlairIcon from '../../../../../../atomic/atoms/FlairIcon';
import { useDurationFormatter } from '../../../../../../hooks/useDurationFormatter';
import { useTranslation } from 'react-i18next';
import type {
  OpenShiftState,
  OpenShiftNotAvailableReason,
} from '../../../../components/Shifts/Common/types';
import {
  OPEN_SHIFT_STATE_VARIANT,
  getOpenShiftStateLabels,
} from '../openShiftConstants';

type OpenShiftSidePanelProps = {
  name: string;
  startDate: string;
  endDate: string;
  targetName: string | null;
  breakPeriodMinutes: number;
  state: OpenShiftState;
  notAvailableReason?: OpenShiftNotAvailableReason;
  onRequest: () => void;
  onCancelRequest: () => void;
  isPending: boolean;
};

export const OpenShiftSidePanel: React.FC<
  React.PropsWithChildren<OpenShiftSidePanelProps>
> = ({
  startDate,
  endDate,
  targetName,
  breakPeriodMinutes,
  state,
  notAvailableReason,
  onRequest,
  onCancelRequest,
  isPending,
}) => {
  const t = useNamespacedTranslation('schedulerShifts.openShift');
  const { t: tGlobal } = useTranslation();
  const formatDuration = useDurationFormatter(tGlobal);

  const displayStart = new Date(startDate);
  const displayEnd = new Date(endDate);

  const stateLabel = getOpenShiftStateLabels(t);

  const reasonLabel: Record<string, string> = {
    'past-shift': t('notAvailableReason.pastShift'),
    overlapped: t('notAvailableReason.overlapped'),
    'no-free-slots': t('notAvailableReason.noFreeSlots'),
  };

  return (
    <div className="p-4 d-flex flex-column" style={{ minHeight: '100%' }}>
      <div className="flex-grow-1">
        <Section
          icon={<FlairIcon icon="calendar" size="sm" />}
          value={
            <>
              <div>
                <FormattedDate day={displayStart} format="day-with-weekday" />
              </div>
              <div>{`${formatTime24h(displayStart)} - ${formatTime24h(
                displayEnd,
              )}`}</div>
            </>
          }
        />
        {targetName && (
          <Section
            icon={<FlairIcon icon="business-outline" size="sm" />}
            value={targetName}
          />
        )}
        {breakPeriodMinutes > 0 && (
          <Section
            icon={<FlairIcon icon="cafe-outline" size="sm" />}
            value={formatDuration(breakPeriodMinutes)}
          />
        )}
        <Section
          icon={<FlairIcon icon="information-circle-outline" size="sm" />}
          value={
            <Badge bg={OPEN_SHIFT_STATE_VARIANT[state]}>
              {stateLabel[state]}
            </Badge>
          }
        />
        {notAvailableReason && reasonLabel[notAvailableReason] && (
          <div className="text-muted small mt-1 ms-4 ps-3">
            {reasonLabel[notAvailableReason]}
          </div>
        )}
      </div>
      {state === 'available' && (
        <div className="mt-auto pt-3 border-top">
          <Button
            variant="primary"
            className="w-100"
            onClick={onRequest}
            disabled={isPending}>
            {isPending ? t('requesting') : t('requestButton')}
          </Button>
        </div>
      )}
      {state === 'requested' && (
        <div className="mt-auto pt-3 border-top">
          <Button
            variant="outline-danger"
            className="w-100"
            onClick={onCancelRequest}
            disabled={isPending}>
            {isPending ? t('canceling') : t('cancelRequestButton')}
          </Button>
        </div>
      )}
    </div>
  );
};

type SectionProps = {
  label?: string;
  value: React.ReactNode;
  icon: React.ReactNode;
};

const Section: React.FC<React.PropsWithChildren<SectionProps>> = ({
  label,
  value,
  icon,
}) => {
  if (!value) {
    return null;
  }

  return (
    <div className="d-flex align-items-start mb-3 gap-3">
      {!!icon && <div className="text-muted flex-shrink-0 mt-1">{icon}</div>}
      <div className="flex-grow-1 d-flex flex-column gap-1">
        {label && <div className="fw-semibold mb-1">{label}</div>}
        <div style={{ lineHeight: 1.4 }}>{value}</div>
      </div>
    </div>
  );
};
